import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { BillingService } from '../../../../services/billing.service';
import { LookupService } from '../../../../services/lookup.service';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { UserService } from '../../../../services/user.service';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';

@Component({
  selector: 'ppl-report-filters',
  standalone: true,
  imports: [
    NzFormModule,
    FormsModule,
    ReactiveFormsModule,
    NzRadioModule,
    NzSelectModule,
    CommonModule,
    NzGridModule,
    DropdownSelectComponent,
  ],
  templateUrl: './report-filters.component.html',
  styleUrl: './report-filters.component.css',
})
export class ReportFiltersComponent implements OnInit, OnChanges {
  @Input() filterFields: any[] = [];
  @Input() report: any;
  @Output() filterChanged = new EventEmitter<{
    field: string;
    value: any;
    formValues: any;
  }>();
  reportFilterForm!: FormGroup;
  programName: any;
  claimTypes = [
    { label: 'All', value: 'All' },
    { label: 'SC', value: 'SC' },
    { label: 'PMPM', value: 'PMPM' },
  ];
  mcoList: any[] = [];
  previousFormValue: any = {};
  metricType: any;
  ignoreReasonList: any[] = [];
  showMCOFilter = true;
  showResubReasonFilter = false;
  resubReasonList: any[] = [];
  resubDates: any[] = [];
  @Input() batchDates: any;

  constructor(
    private fb: FormBuilder,
    private lookupService: LookupService,
    private billingService: BillingService,
    private userService: UserService
  ) {}

  ngOnInit() {
    const formFields = this.filterFields.reduce((acc, f) => {
      if (f.show) {
        acc = { ...acc, [f.field]: [f.default ? f.default : null] };
      }
      return acc;
    }, {});
    this.reportFilterForm = this.fb.group(formFields);
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.getMetricType(this.report);
    this.getDropdownData();
    this.previousFormValue = { ...this.reportFilterForm.value };
    this.reportFilterForm.valueChanges.subscribe((currentValue) => {
      if (this.reportFilterForm.value.metricType === 'MCO') {
        this.showMCOFilter = false;
        this.showResubReasonFilter = true;
      } else {
        this.showMCOFilter = true;
        this.showResubReasonFilter = false;
      }
      const changedField = Object.keys(currentValue).find(
        (key) => this.previousFormValue[key] !== currentValue[key]
      );

      if (changedField === 'metricType' && this.report === 'resubReport') {
        this.reportFilterForm
          .get('mcoName')
          ?.setValue(this.mcoList[0].orgName, { emitEvent: false });
        this.reportFilterForm
          .get('resubReason')
          ?.setValue('All', { emitEvent: false });
      }

      if (changedField) {
        this.filterChanged.emit({
          field: changedField,
          value: currentValue[changedField],
          formValues: currentValue,
        });

        this.previousFormValue = { ...currentValue };
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filterFields'] && this.filterFields?.length) {
      const batchDateField = this.filterFields.find(
        (f) => f.field === 'batchDate'
      );

      if (
        batchDateField?.default &&
        this.reportFilterForm.contains('batchDate')
      ) {
        this.reportFilterForm
          .get('batchDate')
          ?.setValue(batchDateField.default);
      }

    const mcoNameField = this.filterFields.find(f => f.field === 'mcoName');
      if (mcoNameField?.default && this.reportFilterForm?.contains('mcoName')) {
        this.reportFilterForm.get('mcoName')?.setValue(mcoNameField?.default ?? null);
      }
    }
  }

  getDropdownData() {
    this.billingService
      .getIgnoreReasonDropdown(this.programName)
      .subscribe((data: any) => {
        const ignoreReasonList = data?.responsedata;
        this.ignoreReasonList = [{ ignoreReason: 'All' }, ...ignoreReasonList];
      });
    this.lookupService.getOrgData(this.programName).subscribe((data: any) => {
      if (data.status === 200) {
        this.mcoList = data.responsedata.filter(
          (item: any) => item.orgTypeLookup === 'MCO'
        );
      }
    });
    this.billingService
      .getResubReason(this.programName)
      .subscribe((data: any) => {
        const resubReasonList = data?.responsedata;
        this.resubReasonList = [{ resubReason: 'All' }, ...resubReasonList];
      });
    this.billingService
      .getResubDates(this.programName)
      .subscribe((data: any) => {
        const resubDate = data?.responsedata;
        this.resubDates = [{ date: 'All' }, ...resubDate];
      });
  }

  metricChanged(value: boolean) {
    this.showMCOFilter = value;
  }

  getMetricType(type: any) {
    if (type === 'denialReport') {
      this.metricType = [
        { key: 'code', label: 'CARC-RARC' },
        { key: 'mco', label: 'MCO' },
      ];
    } else if (type === 'ignoreTimesheetReport') {
      this.metricType = [
        { key: 'IGNORE_REASON', label: 'Ignore Reason' },
        { key: 'MCO', label: 'MCO' },
      ];
    } else if (type === 'resubReport') {
      this.metricType = [
        { key: 'RESUB_REASON', label: 'Resub Reason' },
        { key: 'MCO', label: 'MCO' },
      ];
    } else if (type === 'activityReport') {
      this.metricType = [
        { key: 'batchDate', label: 'Batch Date' },
        { key: 'payrollEndDate', label: 'Payroll End Date' },
        { key: 'monthName', label: 'Month' },
      ];
    } else {
      this.metricType = [];
    }
  }
}
